import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // <-- import like this

const ShopLeadsList = () => {
  const [leads, setLeads] = useState([]);
  const [shops, setShops] = useState([]);
  const [shopDetails, setShopDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filterShopCode, setFilterShopCode] = useState("");
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [statusValue, setStatusValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [exportDropdown, setExportDropdown] = useState(false);
  const [showSMModal, setShowSMModal] = useState(false); // Added
  const [smDetails, setSmDetails] = useState(null); // Added
  const [smNameFilter, setSmNameFilter] = useState(""); // Added
  const [leadNameFilter, setLeadNameFilter] = useState(""); // Added
  const detailsModalRef = useRef();

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const res = await axios.get(
          "https://api.dremerz.net/api/DZ_8921_Mybankshoppy_Save_QR_LeadFromShops"
        );
        setLeads(Array.isArray(res.data) ? res.data : []);
      } catch {
        setLeads([]);
      }
    };
    const fetchShops = async () => {
      try {
        const res = await axios.get(
          "https://api.dremerz.net/api/DZ_8921_Mybankshoppy_Create_Shops_ShopLists"
        );
        setShops(Array.isArray(res.data) ? res.data : []);
      } catch {
        setShops([]);
      }
    };
    fetchLeads();
    fetchShops();
  }, []);

  const handleShowShopDetails = (shopCode) => {
    const shop = shops.find((s) => s.shopCode === shopCode);
    setShopDetails(shop || null);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setShopDetails(null);
  };

  // Close modal on outside click
  useEffect(() => {
    if (!showDetailsModal) return;
    const handler = (e) => {
      if (
        detailsModalRef.current &&
        !detailsModalRef.current.contains(e.target)
      ) {
        handleCloseDetailsModal();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDetailsModal]);

  // Export to Excel
  const handleExportExcel = () => {
    const exportData = filteredLeads.toReversed().map((lead) => {
      const shop = shops.find((s) => s.shopCode === lead.shopCode) || {};
      return {
        ID: lead.id,
        ShopCode: lead.shopCode,
        Name: lead.name,
        Email: lead.email,
        Mobile: lead.mobile,
        LoanType: lead.loanType,
        LoanAmount: lead.loanAmount,
        SMName: shop.smname || "",
        SMEmail: shop.smemail || "",
        SMMobile: shop.smmobile || "",
        Date: lead.createdDate,
        Time: lead.createdTime,
        Status:
          lead.loanStatus && lead.loanStatus.trim()
            ? lead.loanStatus
            : "Pending",
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
    XLSX.writeFile(workbook, "ShopLeads.xlsx");
    setExportDropdown(false);
  };

  // Export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "A4",
    });
    const tableColumn = [
      "ID",
      "ShopCode",
      "Name",
      "Email",
      "Mobile",
      "LoanType",
      "LoanAmount",
      "SM Name",
      "SM Email",
      "SM Mobile",
      "Date",
      "Time",
      "Status",
    ];
    const tableRows = filteredLeads.toReversed().map((lead) => {
      const shop = shops.find((s) => s.shopCode === lead.shopCode) || {};
      return [
        lead.id,
        lead.shopCode,
        lead.name,
        lead.email,
        lead.mobile,
        lead.loanType,
        lead.loanAmount,
        shop.smname || "",
        shop.smemail || "",
        shop.smmobile || "",
        lead.createdDate,
        lead.createdTime,
        lead.loanStatus && lead.loanStatus.trim() ? lead.loanStatus : "Pending",
      ];
    });
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [13, 110, 253], fontSize: 8, cellPadding: 3 },
      margin: { left: 10, right: 10 },
      tableWidth: "auto",
    });
    doc.save("ShopLeads.pdf");
    setExportDropdown(false);
  };

  // Filtered leads by status, SM Name, and Lead Name
  const filteredLeads = leads
    .filter((lead) =>
      filterShopCode ? lead.shopCode === filterShopCode : true
    )
    .filter((lead) =>
      statusFilter
        ? lead.loanStatus && lead.loanStatus.trim() === statusFilter
        : true
    )
    .filter((lead) => {
      if (!smNameFilter) return true;
      const shop = shops.find((s) => s.shopCode === lead.shopCode);
      return (
        shop &&
        shop.smname &&
        shop.smname.toLowerCase().includes(smNameFilter.toLowerCase())
      );
    })
    .filter((lead) =>
      leadNameFilter
        ? (lead.name || "").toLowerCase().includes(leadNameFilter.toLowerCase())
        : true
    );

  // Update loan status in DB
  const handleStatusChange = async (lead, newStatus) => {
    try {
      await axios.put(
        `https://api.dremerz.net/api/DZ_8921_Mybankshoppy_Save_QR_LeadFromShops/${lead.id}`,
        { ...lead, loanStatus: newStatus }
      );
      setEditingStatusId(null);
      setStatusValue("");
      // Refresh leads after update
      const res = await axios.get(
        "https://api.dremerz.net/api/DZ_8921_Mybankshoppy_Save_QR_LeadFromShops"
      );
      setLeads(Array.isArray(res.data) ? res.data : []);
    } catch {
      alert("Failed to update status");
    }
  };

  // Show SM details popup
  const handleShowSM = (shopCode) => {
    const shop = shops.find((s) => s.shopCode === shopCode);
    if (shop) {
      setSmDetails(shop);
      setShowSMModal(true);
    }
  };

  const handleCloseSM = () => {
    setShowSMModal(false);
    setSmDetails(null);
  };

  return (
    <div className="container mt-4">
      {/* Stepper-style sub-navbar */}
      <div
        className="d-flex justify-content-between align-items-center mb-4"
        style={{ gap: 0 }}
      >
        <Link
          to="/createshop"
          className="btn btn-outline-primary btn-sm flex-fill mx-1"
          style={{ borderRadius: "20px" }}
        >
          1. Create Agent
        </Link>
        <Link
          to="/shoplist"
          className="btn btn-outline-primary btn-sm flex-fill mx-1"
          style={{ borderRadius: "20px" }}
        >
          2. Agent List
        </Link>
        <Link
          to="/DownloadShopQr"
          className="btn btn-outline-primary btn-sm flex-fill mx-1"
          style={{ borderRadius: "20px" }}
        >
          3. Download QR
        </Link>

        <Link
          to="/shopleadslist"
          className="btn btn-outline-primary btn-sm flex-fill mx-1"
          style={{ borderRadius: "20px" }}
        >
          4. Leads List
        </Link>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">Agent Leads List</h3>
        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          <div style={{ minWidth: 200 }}>
            <select
              className="form-select"
              value={filterShopCode}
              onChange={(e) => setFilterShopCode(e.target.value)}
            >
              <option value="">All Agent Codes</option>
              {Array.from(new Set(shops.map((s) => s.shopCode)))
                .sort()
                .map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
            </select>
          </div>
          <div style={{ minWidth: 150 }}>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Processing">Processing</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
          {/* SM Name Filter */}
          <div style={{ minWidth: 180 }}>
            <input
              type="text"
              className="form-control"
              placeholder="Filter by SM Name"
              value={smNameFilter}
              onChange={(e) => setSmNameFilter(e.target.value)}
              style={{ fontSize: "13px" }}
            />
          </div>
          {/* Lead Name Filter */}
          <div style={{ minWidth: 180 }}>
            <input
              type="text"
              className="form-control"
              placeholder="Filter by Lead Name"
              value={leadNameFilter}
              onChange={(e) => setLeadNameFilter(e.target.value)}
              style={{ fontSize: "13px" }}
            />
          </div>
          <div className="dropdown">
            <button
              className="btn btn-outline-success btn-sm dropdown-toggle"
              type="button"
              onClick={() => setExportDropdown((prev) => !prev)}
              style={{ borderRadius: "20px" }}
            >
              Export
            </button>
            {exportDropdown && (
              <ul
                className="dropdown-menu show"
                style={{ position: "absolute", left: 0, top: "100%" }}
              >
                <li>
                  <button className="dropdown-item" onClick={handleExportExcel}>
                    Excel
                  </button>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleExportPDF}>
                    PDF
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </div>
      <div className="table-responsive">
        <table
          className="table table-bordered table-striped align-middle"
          style={{ fontSize: "13px" }}
        >
          <thead className="table-light">
            <tr style={{ whiteSpace: "nowrap" }}>
              <th style={{ minWidth: 40 }}>ID</th>
              <th style={{ minWidth: 80 }}>Agent Code</th>
              <th style={{ minWidth: 90 }}>Name</th>
              <th style={{ minWidth: 110 }}>Email</th>
              <th style={{ minWidth: 80 }}>Mobile</th>
              <th style={{ minWidth: 90 }}>Loan Type</th>
              <th style={{ minWidth: 90 }}>Loan Amount</th>
              <th style={{ minWidth: 70 }}>Date</th>
              <th style={{ minWidth: 70 }}>Time</th>
              <th style={{ minWidth: 80 }}>Status</th>
              <th style={{ minWidth: 80 }}>SM Name</th> {/* Added */}
              <th style={{ minWidth: 80 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.toReversed().map((lead) => {
              const shop = shops.find((s) => s.shopCode === lead.shopCode);
              return (
                <tr key={lead.id}>
                  <td style={{ verticalAlign: "middle" }}>{lead.id}</td>
                  <td style={{ verticalAlign: "middle" }}>
                    <button
                      className="btn btn-link p-0"
                      style={{
                        textDecoration: "underline",
                        color: "#0d6efd",
                        background: "none",
                        border: "none",
                        fontSize: "13px",
                      }}
                      onClick={() => handleShowShopDetails(lead.shopCode)}
                    >
                      {lead.shopCode}
                    </button>
                  </td>
                  <td style={{ verticalAlign: "middle" }}>{lead.name}</td>
                  <td style={{ verticalAlign: "middle" }}>{lead.email}</td>
                  <td style={{ verticalAlign: "middle" }}>{lead.mobile}</td>
                  <td style={{ verticalAlign: "middle" }}>{lead.loanType}</td>
                  <td style={{ verticalAlign: "middle" }}>{lead.loanAmount}</td>
                  <td style={{ verticalAlign: "middle" }}>
                    {lead.createdDate}
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    {lead.createdTime}
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    {lead.loanStatus && lead.loanStatus.trim()
                      ? lead.loanStatus
                      : "Pending"}
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    {shop && shop.smname ? (
                      <button
                        className="btn btn-link p-0"
                        style={{
                          textDecoration: "underline",
                          color: "#198754",
                          background: "none",
                          border: "none",
                          fontSize: "13px",
                        }}
                        onClick={() => handleShowSM(lead.shopCode)}
                      >
                        {shop.smname}
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    <select
                      className="form-select form-select-sm d-inline-block"
                      value={
                        lead.loanStatus && lead.loanStatus.trim()
                          ? lead.loanStatus
                          : "Pending"
                      }
                      onChange={(e) => handleStatusChange(lead, e.target.value)}
                      style={{
                        fontSize: "12px",
                        padding: "2px 8px",
                        minWidth: 110, // Increased width to avoid overlap
                        backgroundPosition: "right 6px center", // Move arrow further right
                      }}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Processing">Processing</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* SM Details Modal */}
      {showSMModal && smDetails && (
        <div
          className="modal show fade"
          style={{
            display: "block",
            background: "rgba(0,0,0,0.5)",
            zIndex: 2000,
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: 350 }}
          >
            <div className="modal-content" style={{ borderRadius: 10 }}>
              <div className="modal-header">
                <h5 className="modal-title"></h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseSM}
                ></button>
              </div>
              <div className="modal-body text-center">
                <div className="mb-2">
                  <strong>Name:</strong> {smDetails.smname || "N/A"}
                </div>
                <div className="mb-2">
                  <strong>Mobile:</strong> {smDetails.smmobile || "N/A"}
                </div>
                <div className="mb-2">
                  <strong>Email:</strong> {smDetails.smemail || "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Shop Details Modal */}
      {showDetailsModal && shopDetails && (
        <div
          className="modal show fade"
          style={{
            display: "block",
            background: "rgba(0,0,0,0.5)",
            zIndex: 1060,
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: 400 }}
          >
            <div className="modal-content" ref={detailsModalRef}>
              <div className="modal-header">
                <h5 className="modal-title" style={{ marginLeft: "120px" }}>
                  Shop Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseDetailsModal}
                ></button>
              </div>
              <div className="modal-body">
                <div>
                  <strong>Shop Code:</strong> {shopDetails.shopCode}
                </div>
                <div>
                  <strong>Shop Name:</strong> {shopDetails.shopname}
                </div>
                <div>
                  <strong>Owner Name:</strong> {shopDetails.ownername}
                </div>
                <div>
                  <strong>Address:</strong> {shopDetails.shopfulladdress}
                </div>
                <div>
                  <strong>PIN:</strong> {shopDetails.shoppin}
                </div>
                <div>
                  <strong>Contact:</strong> {shopDetails.contactnumber}
                </div>
                <div>
                  <strong>Created Date:</strong> {shopDetails.createdDate}
                </div>
                <div>
                  <strong>Created Time:</strong> {shopDetails.createdTime}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopLeadsList;
